import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  MessageSquarePlus,
  Bug,
  Lightbulb,
  Sparkles,
  HelpCircle,
  Send,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  Trash2,
  Filter,
  User
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'BUG', label: 'Bug Report', icon: Bug, color: 'bg-red-100 text-red-700' },
  { id: 'IMPROVEMENT', label: 'Improvement', icon: Lightbulb, color: 'bg-amber-100 text-amber-700' },
  { id: 'FEATURE', label: 'Feature Request', icon: Sparkles, color: 'bg-blue-100 text-blue-700' },
  { id: 'QUESTION', label: 'Question', icon: HelpCircle, color: 'bg-purple-100 text-purple-700' },
];

const PAGES = [
  'Dashboard',
  'Jobs',
  'Calendar',
  'Contacts',
  'Opportunities',
  'Proposals',
  'Timesheet',
  'Field Tracker',
  'Field Reports',
  '811 Tickets',
  'Maintenance',
  'Office Dashboard',
  'Team',
  'Settings',
  'Other'
];

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'bg-slate-100 text-slate-600' },
  { id: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-600' },
  { id: 'high', label: 'High', color: 'bg-amber-100 text-amber-600' },
  { id: 'critical', label: 'Critical', color: 'bg-red-100 text-red-600' },
];

const STATUSES = [
  { id: 'new', label: 'New', icon: Clock, color: 'bg-blue-100 text-blue-700' },
  { id: 'reviewed', label: 'Reviewed', icon: Eye, color: 'bg-purple-100 text-purple-700' },
  { id: 'planned', label: 'Planned', icon: Clock, color: 'bg-amber-100 text-amber-700' },
  { id: 'completed', label: 'Completed', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  { id: 'wont_fix', label: "Won't Fix", icon: XCircle, color: 'bg-slate-100 text-slate-600' },
];

export default function Feedback() {
  const { token, isAdmin, isSuperUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Form state
  const [category, setCategory] = useState('');
  const [page, setPage] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');

  const canViewAll = isAdmin || isSuperUser;

  const loadFeedback = async () => {
    if (!canViewAll) return;
    
    setLoading(true);
    try {
      let url = `${API}/api/feedback/list`;
      const params = [];
      if (filterStatus !== 'all') params.push(`status=${filterStatus}`);
      if (filterCategory !== 'all') params.push(`category=${filterCategory}`);
      if (params.length) url += '?' + params.join('&');
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbackList(res.data.feedback || []);
      setStatusCounts(res.data.status_counts || {});
    } catch (error) {
      console.error('Failed to load feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canViewAll) {
      loadFeedback();
    }
  }, [token, filterStatus, filterCategory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category || !description.trim()) {
      toast.error('Please select a category and enter a description');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/feedback/submit`, {
        category,
        page: page || null,
        description: description.trim(),
        priority
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Feedback submitted! Thank you.');
      setCategory('');
      setPage('');
      setDescription('');
      setPriority('medium');
      
      if (canViewAll) {
        loadFeedback();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (feedbackId, newStatus) => {
    try {
      await axios.put(`${API}/api/feedback/${feedbackId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      loadFeedback();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm('Delete this feedback?')) return;
    
    try {
      await axios.delete(`${API}/api/feedback/${feedbackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feedback deleted');
      loadFeedback();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getCategoryInfo = (catId) => CATEGORIES.find(c => c.id === catId) || CATEGORIES[0];
  const getPriorityInfo = (priId) => PRIORITIES.find(p => p.id === priId) || PRIORITIES[1];
  const getStatusInfo = (statId) => STATUSES.find(s => s.id === statId) || STATUSES[0];

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Feedback</h1>
          <p className="text-slate-500">Submit feedback, report bugs, or request features</p>
        </div>
        <MessageSquarePlus className="w-8 h-8 text-amber-500" />
      </div>

      {/* Submit Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed']">Submit Feedback</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category *
                </label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <cat.icon className="w-4 h-4" />
                          {cat.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Page */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Related Page
                </label>
                <Select value={page} onValueChange={setPage}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select page..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGES.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Priority
                </label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description *
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue, suggestion, or question in detail..."
                rows={4}
                className="resize-none"
              />
            </div>

            <Button type="submit" disabled={submitting} className="w-full md:w-auto">
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Feedback
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Admin View - All Feedback */}
      {canViewAll && (
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className="text-lg font-['Barlow_Condensed']">All Feedback</CardTitle>
              
              {/* Status Counts */}
              <div className="flex flex-wrap gap-2">
                {STATUSES.map(s => (
                  <Badge 
                    key={s.id} 
                    variant="outline"
                    className={`cursor-pointer ${filterStatus === s.id ? s.color : ''}`}
                    onClick={() => setFilterStatus(filterStatus === s.id ? 'all' : s.id)}
                  >
                    {s.label}: {statusCounts[s.id] || 0}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex gap-2 mt-4">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : feedbackList.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No feedback found</p>
            ) : (
              <div className="space-y-3">
                {feedbackList.map(item => {
                  const catInfo = getCategoryInfo(item.category);
                  const priInfo = getPriorityInfo(item.priority);
                  const statInfo = getStatusInfo(item.status);
                  const CatIcon = catInfo.icon;
                  
                  return (
                    <div key={item.id} className="border rounded-lg p-4 hover:bg-slate-50">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                        <div className="flex-1">
                          {/* Header badges */}
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <Badge className={catInfo.color}>
                              <CatIcon className="w-3 h-3 mr-1" />
                              {catInfo.label}
                            </Badge>
                            {item.page && (
                              <Badge variant="outline">{item.page}</Badge>
                            )}
                            <Badge className={priInfo.color}>{priInfo.label}</Badge>
                          </div>
                          
                          {/* Description */}
                          <p className="text-slate-700 whitespace-pre-wrap">{item.description}</p>
                          
                          {/* User & Date */}
                          <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {item.user_name}
                            </span>
                            <span>
                              {new Date(item.created_at).toLocaleDateString()} {new Date(item.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </span>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Select 
                            value={item.status} 
                            onValueChange={(val) => updateStatus(item.id, val)}
                          >
                            <SelectTrigger className={`w-32 h-8 text-xs ${statInfo.color}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteFeedback(item.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
